const MAX_RETRIES = 20; // ~4s total (20 * 200ms), covers slow CDN loads
const RETRY_DELAY_MS = 200;

/**
 * Typesets math inside `node` using MathJax, retrying for a short while if
 * the MathJax script (loaded from a CDN in public/index.html) hasn't
 * finished initializing yet.
 *
 * Previously call-sites called `window.MathJax.typesetClear(...)` /
 * `window.MathJax.typesetPromise(...)` directly and unguarded. If MathJax
 * wasn't ready yet (e.g. slow network) this threw and the effect never
 * retried, leaving raw, un-rendered TeX (e.g. "\[ ... \]") on screen.
 */
export function typesetMath(node, attempts = 0) {
  if (!node) return;

  const isReady = typeof window !== 'undefined' && window.MathJax && window.MathJax.typesetPromise;

  if (isReady) {
    try {
      window.MathJax.typesetClear([node]);
      window.MathJax.typesetPromise([node]).catch((err) => {
        console.error('MathJax typeset error:', err);
      });
    } catch (err) {
      console.error('MathJax typeset error:', err);
    }
    return;
  }

  if (attempts < MAX_RETRIES) {
    setTimeout(() => typesetMath(node, attempts + 1), RETRY_DELAY_MS);
  }
}

const MATH_DELIMITER_PATTERN = /\\\(|\\\)|\\\[|\\\]|\$\$|\$/;

/**
 * MathJax's default `skipHtmlTags` config excludes `pre`/`code` elements from
 * typesetting. Content authored/pasted through CKEditor can end up with
 * equations wrapped in `<pre>` (e.g. AI-generated markdown with fenced code
 * blocks turned into HTML on paste), which silently stops MathJax from ever
 * finding them - not a timing/config bug, MathJax is just doing what its
 * default config tells it to.
 *
 * This walks a raw HTML string, and for any `<pre>`/`<code>` element whose
 * text content looks like TeX math (contains \( \) \[ \] or $ delimiters),
 * swaps it for a plain `<span>` carrying the same inner HTML, so MathJax can
 * see and typeset it. Elements that don't look like math (actual code
 * snippets) are left alone.
 */
export function unwrapMathFromPre(html) {
  if (!html || typeof window === 'undefined' || !window.DOMParser) return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const candidates = doc.querySelectorAll('pre, code');

    candidates.forEach((el) => {
      const text = el.textContent || '';
      if (MATH_DELIMITER_PATTERN.test(text)) {
        const replacement = doc.createElement('span');
        replacement.innerHTML = el.innerHTML;
        el.replaceWith(replacement);
      }
    });

    return doc.body.innerHTML;
  } catch (err) {
    console.error('unwrapMathFromPre error:', err);
    return html;
  }
}

export default typesetMath;
